import { useCallback, useEffect, useState } from "react";
import { AxiosProgressEvent } from "axios";
import { CONSTANTS } from "src/constants";
import { commonLocales } from "src/locales/commonLocales";
import {
  City,
  CityData,
  ProgressState,
  UseFileUploadProps,
} from "src/models/common.model";
import { CommonInfoDetails } from "src/models/commonInfoDetails.models";
import API_SERVICES from "src/services";
import handleResponse from "src/services/apiConfig/api.response.service";

export const useCities = (
  searchTerm: string = CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE
) => {
  const [loading, setLoading] = useState(CONSTANTS.COMMON_CONSTANTS.YES);
  const [error, setError] = useState<string | null>(
    CONSTANTS.INITIAL_VALUES.NULL_STATE
  );
  const [cities, setCities] = useState<City[]>([]);
  const fetchCities = useCallback(async () => {
    setLoading(CONSTANTS.COMMON_CONSTANTS.YES);
    setError(CONSTANTS.INITIAL_VALUES.NULL_STATE);

    try {
      const data = await API_SERVICES.COMMON_INFO.GET_CITY(searchTerm);
      const responseData = handleResponse(data);

      if (responseData.ok) {
        setCities(responseData.data);
      } else {
        setError(responseData.error);
      }
    } catch (err) {
      setError(CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE);
      console.error(err);
    } finally {
      setLoading(CONSTANTS.COMMON_CONSTANTS.NO);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  return { cities, loading, error };
};
export const useCityDetails = (id: string) => {
  const [loading, setLoading] = useState(CONSTANTS.COMMON_CONSTANTS.YES);
  const [error, setError] = useState<string | null>(
    CONSTANTS.INITIAL_VALUES.NULL_STATE
  );
  const [city, setCity] = useState<CityData>();
  useEffect(() => {
    fetchCity();
  }, [id]);
  const fetchCity = useCallback(async () => {
    setLoading(true);
    setError(CONSTANTS.INITIAL_VALUES.NULL_STATE);
    if (id) {
      try {
        const data = await API_SERVICES.COMMON_INFO.GET_CITY_DETAILS(id);
        const responseData = handleResponse(data);

        if (responseData.ok) {
          setCity(responseData.data);
        } else {
          setError(responseData.error);
        }
      } catch (err) {
        setError(CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE);
        console.error(err);
      } finally {
        setLoading(CONSTANTS.COMMON_CONSTANTS.NO);
      }
    }
  }, [id]);

  return { city, loading, error };
};
export const useCommonInfo = () => {
  const [loading, setLoading] = useState(CONSTANTS.COMMON_CONSTANTS.NO);
  const [error, setError] = useState<string | null>(
    CONSTANTS.INITIAL_VALUES.NULL_STATE
  );
  const [commonInfo, setCommonInfo] = useState<CommonInfoDetails>();
  const fetchCommonInfo = useCallback(async () => {
    setLoading(CONSTANTS.COMMON_CONSTANTS.YES);
    setError(CONSTANTS.INITIAL_VALUES.NULL_STATE);

    try {
      const data = await API_SERVICES.COMMON_INFO.GET_COMMON_DATA_INFO();
      const responseData = handleResponse(data);

      if (responseData.ok) {
        setCommonInfo(responseData.data);
      } else {
        setError(responseData.error);
      }
    } catch (err) {
      setError(CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE);
      console.error(err);
    } finally {
      setLoading(CONSTANTS.COMMON_CONSTANTS.NO);
    }
  }, []);

  useEffect(() => {
    fetchCommonInfo();
  }, [fetchCommonInfo]);

  return { commonInfo, loading, error };
};

export const useFileUpload = ({ onSuccess, onError }: UseFileUploadProps) => {
  const [progress, setProgress] = useState<ProgressState>({
    progress: CONSTANTS.INITIAL_VALUES.PERCENTAGE,
    color: CONSTANTS.STATUS.SUCCESS,
    message: CONSTANTS.INITIAL_VALUES.STRING,
    status: CONSTANTS.INITIAL_VALUES.STRING,
  });

  const updateProgress = useCallback((progressEvent: AxiosProgressEvent) => {
    const progressPercent = Math.round(
      (progressEvent.loaded * CONSTANTS.FINAL_VALUES.PERCENTAGE) /
        (progressEvent.total ?? CONSTANTS.INITIAL_VALUES.PERCENTAGE)
    );
    setProgress({
      color: CONSTANTS.STATUS.WARNING,
      message: commonLocales.upload.inProgress,
      progress: progressPercent,
      status: CONSTANTS.STATUS.IN_PROGRESS,
    });
  }, []);

  const uploadFile = useCallback(
    async (formData: FormData) => {
      setProgress({
        color: CONSTANTS.STATUS.WARNING,
        message: commonLocales.upload.inProgress,
        progress: CONSTANTS.INITIAL_VALUES.PERCENTAGE,
        status: CONSTANTS.STATUS.IN_PROGRESS,
      });

      try {
        const res = await API_SERVICES.PROFILE.FILE_UPLOAD(formData, {
          onUploadProgress: updateProgress,
        });
        const responseData = handleResponse(res, "", false);
        if (!responseData.ok) {
          setProgress({
            color: CONSTANTS.STATUS.DANGER,
            message: responseData?.data?.message ?? commonLocales.upload.error,
            progress: CONSTANTS.FINAL_VALUES.PERCENTAGE,
            status: CONSTANTS.STATUS.ERROR,
          });
          if (onError) onError(responseData.data);
          return;
        }

        setProgress({
          color: CONSTANTS.STATUS.SUCCESS,
          message: commonLocales.upload.done,
          progress: CONSTANTS.FINAL_VALUES.PERCENTAGE,
          status: CONSTANTS.STATUS.SUCCESS,
        });
        if (onSuccess) onSuccess(responseData.data);
      } catch (error) {
        setProgress({
          color: CONSTANTS.STATUS.DANGER,
          message: commonLocales.upload.error,
          progress: CONSTANTS.FINAL_VALUES.PERCENTAGE,
          status: CONSTANTS.STATUS.ERROR,
        });
        if (onError) onError(error);
      }
    },
    [updateProgress, onError, onSuccess]
  );

  return {
    progress,
    uploadFile,
  };
};
